import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../services/logger';

interface ReconnectionConfig {
  /** Maximum number of reconnection attempts */
  maxAttempts?: number;
  /** Delay between attempts in milliseconds */
  delayMs?: number;
  /** Exponential backoff multiplier */
  backoffMultiplier?: number;
}

interface ReconnectionState {
  /** Current reconnection attempt (0 = not reconnecting) */
  attempt: number;
  /** Whether currently attempting to reconnect */
  isReconnecting: boolean;
  /** Manual retry function */
  retry: () => void;
  /** Reset reconnection state */
  reset: () => void;
}

/**
 * Manages automatic reconnection logic with exponential backoff.
 *
 * @example
 * const reconnection = useReconnection({
 *   maxAttempts: 3,
 *   delayMs: 2000,
 *   backoffMultiplier: 2,
 * });
 *
 * // When connection drops:
 * if (video.error && !video.isConnected) {
 *   reconnection.retry();
 * }
 */
export function useReconnection(
  onReconnect: () => Promise<void>,
  config: ReconnectionConfig = {}
): ReconnectionState {
  const {
    maxAttempts = 3,
    delayMs = 2000,
    backoffMultiplier = 2,
  } = config;

  const [attempt, setAttempt] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptReconnectionRef = useRef<(() => Promise<void>) | null>(null);

  const reset = useCallback(() => {
    setAttempt(0);
    setIsReconnecting(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const attemptReconnection = useCallback(async () => {
    if (attempt >= maxAttempts) {
      setIsReconnecting(false);
      return;
    }

    setIsReconnecting(true);
    const currentAttempt = attempt + 1;
    setAttempt(currentAttempt);

    // Calculate delay with exponential backoff
    const delay = delayMs * Math.pow(backoffMultiplier, currentAttempt - 1);

    try {
      await onReconnect();
      // Success - reset state
      reset();
    } catch (error) {
      logger.error(`Reconnection attempt ${currentAttempt} failed`, { error });

      // Schedule next attempt if not at max
      if (currentAttempt < maxAttempts) {
        timeoutRef.current = setTimeout(() => {
          attemptReconnectionRef.current?.();
        }, delay);
      } else {
        setIsReconnecting(false);
      }
    }
  }, [attempt, maxAttempts, delayMs, backoffMultiplier, onReconnect, reset]);

  // Keep ref updated with latest function
  useEffect(() => {
    attemptReconnectionRef.current = attemptReconnection;
  }, [attemptReconnection]);

  const retry = useCallback(() => {
    reset();
    attemptReconnectionRef.current?.();
  }, [reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    attempt,
    isReconnecting,
    retry,
    reset,
  };
}
